// Import the GrowthBook SDK
import { GrowthBook } from '@growthbook/growthbook';
import { ref } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import { sendSegmentEvent } from './segment';
import { readMeta, readMetaBoolean } from './page';
import { accountId, accountType } from './jwt';
import { readDomainCookie, writeDomainCookie } from './domain_cookie';
import { oneTrustEnabled, performanceCookiesEnabled } from './oneTrust';

// define a global variable to store the GrowthBook instance
// but do not initialize it yet so that we can import this file without erroring out
let gb: GrowthBook | null;

// values from https://docs.growthbook.io/faq#how-do-i-make-my-own-identifier
const COOKIE_NAME = 'gbuuid';
const COOKIE_DAYS = 400; // 400 days is the max cookie duration for chrome

export const growthbookInstance = (): GrowthBook | null => gb;

// Reactive token bumped whenever the GrowthBook instance changes (init or reset).
// Vue computed properties that read isOn()/getFeatureValue() subscribe to it, so flags applied after the app has mounted
// — e.g. once OneTrust consent arrives — reach the UI without a manual re-render.
const flagStateVersion = ref(0);

const bumpReactiveFlagState = (): void => {
  flagStateVersion.value += 1;
};

const generateCookie = (): string => {
  const existingCookieId = readDomainCookie(COOKIE_NAME);
  if (existingCookieId) return existingCookieId;

  const newCookieId = uuidv4();
  writeDomainCookie(COOKIE_NAME, newCookieId, { expires: COOKIE_DAYS });
  return newCookieId;
};

const trackingCallback = (gbuuid: string) => {
  return (experiment, result) => {
    sendSegmentEvent('Experiment Viewed', {
      experimentId: experiment.key,
      variationId: result.key,
      gbuuid,
    });
  };
};

export const resetGrowthbook = () => {
  const hadInstance = gb != null;
  gb = null;
  if (hadInstance) bumpReactiveFlagState();
};

/**
 * JWT-derived GrowthBook targeting attributes shared by sync and async sync paths.
 */
const jwtTargetingAttributes = (): { userId: string | null; role: string | null } => ({
  userId: accountId(),
  role: accountType(),
});

const targetingAttributesAreCurrent = (current: Record<string, unknown>): boolean => {
  const { userId, role } = jwtTargetingAttributes();
  return current.userId === userId && current.role === role;
};

/**
 * Sync path: patch attributes via setAttributes for callers that cannot await
 * (e.g. placement reads). Prefer syncGrowthbookUserAttributes after SPA auth
 * so feature values refresh via updateAttributes.
 */
export const syncGrowthbookIdentity = (): void => {
  if (!gb) {
    return;
  }

  const current = gb.getAttributes?.() || {};
  if (targetingAttributesAreCurrent(current)) {
    return;
  }

  const { userId, role } = jwtTargetingAttributes();
  gb.setAttributes({ ...current, userId, role });
  bumpReactiveFlagState();
};

// this will return the growthbook instance if it has been initialized,
// otherwise it will initialize it and return the instance
export const setupGrowthbook = async (): Promise<GrowthBook | null> => {
  // if we have already initialized GrowthBook, return the existing instance
  if (gb) {
    syncGrowthbookIdentity();
    return gb;
  }

  // client key needed to initialize GrowthBook
  const clientKey = readMeta('growthbook-client-key');
  // determines if we should enable the growthbook dev mode for experimentation and debugging
  const enableDevMode = readMetaBoolean('growthbook-dev-mode');

  // TODO: should we raise an error here instead?
  // As it currently stands, the app can check for null and handle it gracefully
  // if we raise an errror, that will need to be caught to handle it gracefully
  if (!clientKey) {
    console.error('GrowthBook client key missing');
    return null;
  }

  // Create an instance of GrowthBook
  // user id for targeting, this may be null
  const { userId, role } = jwtTargetingAttributes();
  const gbuuid = generateCookie();

  try {
    gb = new GrowthBook({
      clientKey,
      enableDevMode,
      attributes: { userId, gbuuid, role },
      trackingCallback: trackingCallback(gbuuid),
    });

    await gb.init();

    bumpReactiveFlagState();
  } catch (e) {
    console.error('Error initializing GrowthBook:', e);
    gb = null;
  }

  return gb;
};

/**
 * Async path after SPA auth: refresh JWT targeting so experiments re-hash on userId.
 * Uses the same attribute source as syncGrowthbookIdentity, then updateAttributes
 * so feature evaluation picks up the new identity.
 */
export async function syncGrowthbookUserAttributes(): Promise<void> {
  if (!gb) return;

  const existing = gb.getAttributes() ?? {};
  if (targetingAttributesAreCurrent(existing)) {
    return;
  }

  const { userId, role } = jwtTargetingAttributes();

  try {
    await gb.updateAttributes({ userId, role });
    bumpReactiveFlagState();
  } catch (e) {
    console.error('Error syncing GrowthBook user attributes:', e);
  }
}

export const isOn = (key: string): boolean => {
  // Subscribe to the reactive token so Vue computeds re-run when gb changes.
  const version = flagStateVersion.value;
  if (version > -1 && gb) return gb.isOn(key);
  return false;
};

export const getFeatureValue = <T>(key: string, defaultValue: T): T => {
  // Subscribe to the reactive token so Vue computeds re-run when gb changes.
  const version = flagStateVersion.value;
  if (version > -1 && gb) return gb.getFeatureValue(key, defaultValue) as T;
  return defaultValue;
};

const createOrDestroyGrowthbook = async (): Promise<GrowthBook | null> => {
  // if functional cookies are enabled, we can use growthbook
  if (performanceCookiesEnabled()) {
    if (gb) {
      // growthbook has already been initialized
      return gb;
    }
    // growthbook doesn't exist, but it should.
    return setupGrowthbook();
  }

  // clear growthbook if functional cookies have been disabled.
  resetGrowthbook();
  return null;
};

/**
 * Awaitable path for gates that must hash on the current JWT before reading a flag.
 * A new tab can evaluate placement before init finishes; getFeatureValue then
 * returns the default and looks like parent_interaction.
 */
export async function ensureGrowthbookReadyForTargeting(): Promise<void> {
  await createOrDestroyGrowthbook();
  await syncGrowthbookUserAttributes();
}

// wrap the initialization of GrowthBook in a promise so that we can
// wait for the OneTrust library to load before initializing GrowthBook
// this function can be waited on if you rely on GrowthBook on startup
export const initGrowthbook = (): Promise<GrowthBook | null> => {
  // Re-initialize GrowthBook whenever user changes their preferences
  window.addEventListener('OneTrustPreferencesChanged', createOrDestroyGrowthbook);

  // When the OneTrust feature flag is off, skip waiting and init immediately.
  if (!oneTrustEnabled()) {
    return createOrDestroyGrowthbook();
  }

  // If OneTrust is already initialized, proceed immediately
  if (
    typeof window !== 'undefined' &&
    (window as any).OneTrust !== undefined &&
    (window as any).OnetrustActiveGroups !== undefined
  ) {
    return createOrDestroyGrowthbook();
  }

  // Otherwise, wait for the first OneTrustPreferencesChanged event or timeout
  return new Promise<GrowthBook | null>((resolve) => {
    // eslint complains if timeoutId reference is not available before eventHandler
    let timeoutId: ReturnType<typeof setTimeout>;

    const eventHandler = () => {
      clearTimeout(timeoutId);
      window.removeEventListener('OneTrustPreferencesChanged', eventHandler);
      resolve(createOrDestroyGrowthbook());
    };

    timeoutId = setTimeout(() => {
      window.removeEventListener('OneTrustPreferencesChanged', eventHandler);
      resolve(null);
    }, 2000);

    window.addEventListener('OneTrustPreferencesChanged', eventHandler, { once: true });
  });
};

export default {
  resetGrowthbook,
  syncGrowthbookIdentity,
  initGrowthbook,
  syncGrowthbookUserAttributes,
  ensureGrowthbookReadyForTargeting,
  isOn,
  getFeatureValue,
};
